import React from "react";
import { Box, Flex } from "@chakra-ui/react";
import { Outlet } from "react-router-dom";
import BottomNavBar from "../components/BottomNavBar";
import UserFooter from "../components/UserFooter";
import PartnerTopStrip from "../components/partner/PartnerTopStrip";
import PartnerRail from "../components/partner/PartnerRail";
import PartnerAdminPreviewBand from "../components/partner/PartnerAdminPreviewBand";
import PartnerBrowseBand from "../components/partner/PartnerBrowseBand";
import LayWidget from "../components/lay/LayWidget";
import HomeCardStack from "../components/homeCard/HomeCardStack";
import { PartnerProgramProvider } from "../contexts/PartnerProgramContext";
import { useLocation } from "react-router-dom";
import { useUser } from "../contexts/UserContext";
import { isPartnerAdminPreviewWithoutRole, PREVIEW_PROGRAM } from "../lib/partnerNav";

/**
 * 파트너 포털 레이아웃 (2026-09-10 N3 「좌측 레일」 채택 — docs/design/partner-topnav-variants.html)
 *   UserLayout 이 Partner(비어드민) 계정일 때 이걸 대신 그린다. 라우트는 UserLayout 자식 그대로(App.jsx 무변경).
 *   PC:  상단 스트립(로고·파트너·언어·프로필) + 좌측 레일(상태·메뉴·계정·담당자) + 본문
 *   휴대폰: 상단 스트립 + 본문 + 하단 탭 4개(BottomNavBar 의 파트너 분기)
 *   크리에이터용 게이트(PolicyConsentGate · TierRolloutInterstitial)는 두지 않는다 — 대상 판정(isConsentTarget)이 Partner 를 빼기도 하고, 파트너 화면에 뜰 이유가 없다.
 * 문서: docs/pages/main/UserLayout.md · docs/pages/main/PartnerHub.md 「라우팅·내비」
 */
export default function PartnerLayout() {
  const { userInfo } = useUser();
  const location = useLocation();
  // Partner 역할 없는 어드민(대표 계정 등)의 미리보기 — 원장 없이 새 파트너의 첫 화면(서명 전·둘러보기)을 그린다 (2026-09-11 대표 지적)
  const previewProgram = isPartnerAdminPreviewWithoutRole(userInfo?.role, location.pathname) ? PREVIEW_PROGRAM : null;
  return (
    <PartnerProgramProvider previewProgram={previewProgram}>
      <Flex direction="column" minH="100dvh" bg="neutral.50">
        <PartnerTopStrip />
        {/* Admin 겸직이 /partner* 를 볼 때만 — 「어드민 확인용」 띠 (2026-09-11, 대표 결정 ④). 파트너 단독 계정에는 아무것도 그리지 않는다 */}
        <PartnerAdminPreviewBand />
        {/* 둘러보기 띠 — 약관 서명 전(status=pending) 파트너에게 모든 파트너 화면 위에 한 줄 (2026-09-11 시안 V1). 서명 뒤엔 사라진다 */}
        <PartnerBrowseBand />
        <Flex flex={1} align="stretch" w="100%" maxW="1400px" mx="auto" minW={0}>
          <PartnerRail />
          {/* minW={0}: 본문이 넓은 표·긴 링크를 담아도 레이아웃이 뷰포트 폭을 지키게 (UserLayout 과 같은 이유) */}
          <Box flex={1} minW={0} minH={0} display="flex" flexDirection="column" p={{ base: 4, md: 5 }} pb={{ base: 0, md: 6 }}>
            {/* 홈 상단 카드(2026-10-19 · 알림 관리 › 카드 발송) — 파트너 첫 화면(/partner)에서만. 없으면 안 그림 */}
            {location.pathname === "/partner" && <HomeCardStack email={userInfo?.email} />}
            <Outlet />
          </Box>
        </Flex>
        <UserFooter supportLink={false} />
        <BottomNavBar />
        {/* 레이 AI 도우미 — 파트너 범위(scope=partner: 파트너 문서만, 내부 정보·타 파트너 조건 답하지 않음). 서버 lay-docs-chat 이 판정한다.
            크리에이터 UserLayout 과 같은 플래그(REACT_APP_LAY_WIDGET=0 이면 숨김). 대표 지시 2026-09-10 · docs/pages/main/LayWidget.md */}
        {process.env.REACT_APP_LAY_WIDGET !== "0" && <LayWidget />}
      </Flex>
    </PartnerProgramProvider>
  );
}
